import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import axios from "axios";
import { 
  CreditCard, 
  Shield, 
  CheckCircle, 
  ArrowLeft,
  Loader2,
  AlertTriangle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  
  // Promo code functionality
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [validatingPromo, setValidatingPromo] = useState(false);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await axios.get(`${API}/services/${serviceId}`);
        setService(response.data);
      } catch (error) {
        console.error('Error fetching service:', error);
        setError('Service not found');
      } finally {
        setLoading(false);
      }
    };

    if (serviceId) {
      fetchService();
    }
  }, [serviceId]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code");
      return;
    }
    
    if (!email || !validateEmail(email)) {
      setPromoError("Please enter a valid email address first");
      return;
    }

    setValidatingPromo(true);
    setPromoError("");

    try {
      const response = await axios.post(`${API}/promo-codes/validate`, {
        code: promoCode.trim(),
        user_email: email,
        service_type: "basic_per_form"
      });

      if (response.data.valid) {
        setPromoApplied(true);
        setPromoInfo({
          code: response.data.code,
          discount_percent: response.data.discount_percent,
          description: response.data.description
        });
        setPromoError("");
      } else {
        setPromoError(response.data.error || "Invalid promo code");
        setPromoApplied(false);
        setPromoInfo(null);
      }
    } catch (error) {
      console.error('Promo code validation error:', error);
      setPromoError(error.response?.data?.error || "Error validating promo code");
      setPromoApplied(false);
      setPromoInfo(null);
    } finally {
      setValidatingPromo(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode("");
    setPromoApplied(false);
    setPromoInfo(null);
    setPromoError("");
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleCheckout = async () => {
    if (!email) {
      setEmailError("Email is required for receipt and document delivery");
      return;
    }

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setProcessing(true);
    setError("");

    try {
      const originUrl = window.location.origin;
      
      const response = await axios.post(`${API}/checkout/create-session`, {
        service_id: parseInt(serviceId),
        user_email: email,
        origin_url: originUrl,
        promo_code: promoApplied ? promoCode.trim() : null,
        apply_promo: promoApplied
      });

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setError(error.response?.data?.detail || 'Payment processing failed. Please try again.');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Service Not Found</h2>
          <p className="text-gray-600 mb-6">The requested service could not be found.</p>
          <Button onClick={() => navigate('/services')}>
            Back to Services
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Button 
            variant="outline" 
            onClick={() => navigate('/services')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Services
          </Button>
          <h1 className="text-3xl font-bold text-slate-900">Checkout</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CreditCard className="h-5 w-5 mr-2 text-blue-600" />
                Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-teal-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                  <div className="flex items-center mt-2 space-x-4">
                    <Badge variant="outline">{service.complexity}</Badge>
                    <span className="text-sm text-gray-500">{service.duration}</span>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold text-slate-900 mb-3">What's Included:</h4>
                <ul className="space-y-2">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 text-teal-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="border-t pt-4">
                {promoApplied && promoInfo ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-gray-600">
                      <span>Subtotal:</span>
                      <span className="line-through">${service.price}</span>
                    </div>
                    <div className="flex justify-between items-center text-green-600">
                      <span>Promo Code ({promoInfo.code}):</span>
                      <span>-{promoInfo.discount_percent}%</span>
                    </div>
                    <div className="flex justify-between items-center text-lg font-semibold">
                      <span className="text-slate-900">Total:</span>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-orange-500">
                          ${(service.price * (1 - promoInfo.discount_percent / 100)).toFixed(2)}
                        </span>
                        <p className="text-sm text-green-600 font-medium">
                          You save ${(service.price * (promoInfo.discount_percent / 100)).toFixed(2)}!
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-slate-900">Total:</span>
                    <span className="text-2xl font-bold text-orange-500">${service.price}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Shield className="h-5 w-5 mr-2 text-green-600" />
                Secure Payment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Email Address *
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Enter your email address"
                  className={`w-full ${emailError ? 'border-red-500' : ''}`}
                  disabled={processing}
                />
                {emailError && (
                  <p className="text-red-600 text-sm mt-1">{emailError}</p>
                )}
                <p className="text-gray-500 text-xs mt-2">
                  We'll send your receipt and documents to this email address
                </p>
              </div>

              {/* Promo Code Section */}
              <div className="border rounded-lg p-4 bg-gradient-to-r from-green-50 to-yellow-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-900">Have a Promo Code?</h4>
                </div>
                
                {!promoApplied ? (
                  <div className="space-y-3">
                    <div className="flex space-x-2">
                      <Input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="Enter promo code"
                        className="flex-1"
                        disabled={validatingPromo || !email}
                      />
                      <Button
                        onClick={validatePromoCode}
                        disabled={validatingPromo || !promoCode.trim() || !email}
                        variant="outline"
                        className="px-4"
                      >
                        {validatingPromo ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Apply"
                        )}
                      </Button>
                    </div>
                    
                    {promoError && (
                      <p className="text-red-600 text-sm">{promoError}</p>
                    )}
                    
                    <p className="text-xs text-gray-600">
                      Valid for new customers only. One promo code per email/IP address.
                    </p>
                  </div>
                ) : (
                  <div className="bg-green-100 p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-green-800">
                          🎉 Promo Code Applied: {promoInfo.code}
                        </p>
                        <p className="text-sm text-green-700">
                          {promoInfo.description} - You'll save {promoInfo.discount_percent}%!
                        </p>
                      </div>
                      <Button
                        onClick={removePromoCode}
                        variant="ghost"
                        size="sm"
                        className="text-green-700 hover:text-green-800"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">What happens next?</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Secure payment processing via Stripe</li>
                  <li>• Instant document generation</li>
                  <li>• Email receipt with PDF attachments</li>
                  <li>• Step-by-step filing instructions</li>
                </ul>
              </div>

              <Button 
                onClick={handleCheckout}
                disabled={processing || !!emailError || !email}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-4 text-lg"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    Proceed to Payment - ${promoApplied && promoInfo ? 
                      (service.price * (1 - promoInfo.discount_percent / 100)).toFixed(2) : 
                      service.price}
                    <ArrowLeft className="h-5 w-5 ml-2 rotate-180" />
                  </>
                )}
              </Button>

              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                  <Shield className="h-4 w-4" />
                  <span>Secured by Stripe • 256-bit SSL encryption</span>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-xs text-gray-500 text-center">
                  <strong>Money-Back Guarantee:</strong> Not satisfied? Get a full refund within 30 days.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Legal Disclaimer */}
        <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>Legal Disclaimer:</strong> FormForgePro provides legal document preparation services only. 
            We are not attorneys and do not provide legal advice. This service is not a substitute for the 
            advice of an attorney. We recommend consulting with a qualified attorney for complex legal matters.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Checkout;